import { useMemo } from "react";
import moment from "moment";
import { CommissionHistory, CommissionStatus } from "types/affiliation.types";
import { useEarnings } from "features/affiliation/common/hooks";

export const EARNINGS_RANGES = ["1m", "3m", "6m", "1y", "all"] as const;
export type EarningsRange = (typeof EARNINGS_RANGES)[number];

export interface EarningsPoint {
  /** Unix seconds (the start of the day). */
  time: number;
  /** Earnings up to the end of that day. */
  value: number;
}

/**
 * Commissions that count as earnings: confirmed ones and the ones still
 * awaiting confirmation (denied and refunded ones don't).
 */
export const countsAsEarning = (commission: CommissionHistory) =>
  commission.status === CommissionStatus.APPROVED ||
  commission.status === CommissionStatus.PENDING;

/** Total earnings (confirmed + awaiting confirmation). */
export const sumEarnings = (commissions?: CommissionHistory[]) =>
  (commissions || [])
    .filter(countsAsEarning)
    .reduce((sum, commission) => sum + commission.commission, 0);

/**
 * Total earnings over time for the overview chart: one point per day (the
 * running total at the end of that day) from the start of the range to
 * today. "All" starts the day before the first commission, so it rises
 * from zero.
 */
const useEarningsHistory = (range: EarningsRange) => {
  const { commissions, commissionHistoryQuery } = useEarnings();

  const history = useMemo(() => {
    const earnings = (commissions || [])
      .filter(countsAsEarning)
      .slice()
      .sort((a, b) => a.time - b.time);
    const total = earnings.reduce(
      (sum, commission) => sum + commission.commission,
      0
    );
    const today = moment().startOf("day");
    const first = earnings.length
      ? moment.unix(earnings[0].time).startOf("day").subtract(1, "day")
      : today.clone().subtract(RANGE_DAYS["1m"], "days");
    const start =
      range === "all"
        ? moment.min(first, today.clone().subtract(7, "days"))
        : today.clone().subtract(RANGE_DAYS[range], "days");
    const days = Math.max(1, today.diff(start, "days"));
    const stepDays = Math.ceil(days / MAX_POINTS);

    const points: EarningsPoint[] = [];
    let index = 0;
    let running = 0;
    for (let offset = 0; offset <= days; offset += stepDays) {
      // The last point is always today.
      const day = start.clone().add(Math.min(offset, days), "days");
      const end = day.clone().add(1, "day").unix();
      while (index < earnings.length && earnings[index].time < end) {
        running += earnings[index].commission;
        index += 1;
      }
      points.push({ time: day.unix(), value: running });
      if (offset < days && offset + stepDays > days) offset = days - stepDays;
    }
    return { points, total, hasEarnings: earnings.length > 0 };
  }, [commissions, range]);

  return { ...history, loading: commissionHistoryQuery.isLoading };
};

export default useEarningsHistory;

const RANGE_DAYS: Record<Exclude<EarningsRange, "all">, number> = {
  "1m": 30,
  "3m": 91,
  "6m": 182,
  "1y": 365,
};
/** Longer ranges step over days so the line keeps ~one point per pixel. */
const MAX_POINTS = 370;
